import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import {
  AppointmentListRow,
  AppointmentLocationLine,
  CustomerInformationNote,
  ExaminationList,
  ExaminationVideoList,
  RowAction,
  RowActions,
} from "@/components/appointmentRequest/appointmentListItemParts";
import styles from "@/components/appointmentRequest/appointmentListItem.module.css";

interface Props {
  item: any;
  wide: boolean;
  onCancel: (item: any) => void;
  onPlayVideo: (url: string) => void;
}

export default function UpcomingAppointmentListItem(props: Props) {
  const { item } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showConfirmationModalAsync } = useConfirmationModal();

  const appointmentDate = new Date(item.datetime).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const appointmentTime = new Date(item.datetime).toLocaleString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const cancel = async () => {
    if (item.can_be_canceled_by_subject) {
      const isLate =
        item.timely_cancellation_deadline &&
        Date.now() > new Date(item.timely_cancellation_deadline).getTime();
      const confirmed = await showConfirmationModalAsync(
        t(
          isLate
            ? "cancel_appointment_late_confirmation"
            : "cancel_appointment_confirmation"
        )
      );
      if (confirmed) {
        props.onCancel(item);
      }
      return;
    }
    await showConfirmationModalAsync(
      <div>
        {t("appointment_cancel_not_possible")}{" "}
        <a className={styles.emailLink} href="mailto:service@kaerhealth.com">
          service@kaerhealth.com
        </a>
      </div>
    );
  };

  const canBeRescheduled =
    item.appointment_request_id && item.can_be_rescheduled_by_subject;

  const actions: RowAction[] = [];
  if (canBeRescheduled) {
    actions.push({
      key: "change",
      tooltip: t("change_appointment"),
      icon: "pen",
      onClick: () =>
        navigate(`/appointmentrequestopen/${item.appointment_request_id}/pick`),
    });
  }
  actions.push({
    key: "cancel",
    tooltip: t("cancel_appointment"),
    icon: "calendarTimes",
    onClick: cancel,
  });

  return (
    <AppointmentListRow>
      <span className={styles.dateLine}>
        {`${appointmentDate} ${t("at_time", { time: appointmentTime })}`}
      </span>
      <ExaminationList examinations={item.examination_set} />
      <AppointmentLocationLine item={item} />
      <CustomerInformationNote item={item} />
      <ExaminationVideoList
        examinations={item.examination_set}
        onPlay={props.onPlayVideo}
      />
      <RowActions wide={props.wide} actions={actions} />
    </AppointmentListRow>
  );
}
